import React from "react";
import { PdsSyncDashboard } from "./components/pds-sync/PdsSyncDashboard.js";
import { PdsSyncSettings } from "./components/pds-sync/PdsSyncSettings.js";
import { Meister1Settings } from "./components/integration-settings/Meister1Settings.js";
import { SapS4HanaSettings } from "./components/integration-settings/SapS4HanaSettings.js";
import { SexyQuotesSettings } from "./components/integration-settings/SexyQuotesSettings.js";
import { PimProductsSettings } from "./components/integration-settings/PimProductsSettings.js";
import { PdfGeneratorSettings } from "./components/integration-settings/PdfGeneratorSettings.js";

export type IntegrationDashboardProps = {
  integrationId: string;
};

export type IntegrationSettingsProps = {
  integrationId: string;
  mode: "create" | "edit";
};

export type IntegrationMeta = {
  shortName: string;
  color: string;
  category: string;
  description: string;
  /** User-facing display name; overrides the backend integration.name */
  sidebarLabel?: string;
  /**
   * Fully hidden from the UI: no overview card, no sidebar item, no routes.
   * The backend plugin keeps running for already-connected orgs.
   */
  hidden?: boolean;
  /**
   * The connect modal shows no fields; its primary action continues to the
   * create-mode settings page (for integrations whose initial form is too
   * large for the modal)
   */
  connectViaPage?: boolean;
  Dashboard?: React.ComponentType<IntegrationDashboardProps>;
  Settings?: React.ComponentType<IntegrationSettingsProps>;
};

// Component references are lazy getters: the settings components import
// getIntegrationMeta back from this module, and eagerly evaluating the
// bindings here hits the resulting import cycle before initialization
// (ReferenceError under Vite HMR). Getters defer the read to render time.
export const integrationRegistry: Record<string, IntegrationMeta> = {
  pds_sync: {
    shortName: "PDS",
    // To the user, PDS is simply their ERP — plain "PDS" everywhere
    sidebarLabel: "PDS",
    color: "#1565C0",
    category: "ERP",
    description: "Continuous PDS synchronization",
    get Dashboard() {
      return PdsSyncDashboard;
    },
    get Settings() {
      return PdsSyncSettings;
    },
  },
  pds: {
    shortName: "PDS",
    sidebarLabel: "PDS",
    color: "#1565C0",
    category: "ERP",
    description: "PDS import/export integration",
    // v1 predecessor of pds_sync — prod usage check (2026-07-14) found zero
    // organisations; plugin removal is a follow-up ticket
    hidden: true,
  },
  meister1: {
    shortName: "M1",
    sidebarLabel: "Meister1",
    color: "#2E7D32",
    category: "ERP",
    description: "Meister1 ERP integration",
    get Settings() {
      return Meister1Settings;
    },
  },
  crm: {
    shortName: "CRM",
    sidebarLabel: "CRM",
    color: "#6A1B9A",
    category: "CRM",
    description: "CRM integration",
  },
  pdf_generator: {
    shortName: "PDF",
    sidebarLabel: "PDF Generator",
    color: "#C62828",
    category: "Documents",
    description: "PDF template generator",
    get Settings() {
      return PdfGeneratorSettings;
    },
  },
  sexy_quotes: {
    shortName: "SQ",
    sidebarLabel: "Sexy Quotes",
    color: "#E65100",
    category: "Documents",
    description: "Automated quote generation",
    // Not self-service: setup is done by us. The backend plugin keeps running
    // for connected orgs; Settings stays wired so un-hiding is a one-liner.
    hidden: true,
    get Settings() {
      return SexyQuotesSettings;
    },
  },
  pim_products: {
    shortName: "PIM",
    sidebarLabel: "PIM Products",
    color: "#00695C",
    category: "Products",
    description: "PIM product catalogue integration",
    get Settings() {
      return PimProductsSettings;
    },
  },
  sap_s4hana: {
    shortName: "SAP",
    sidebarLabel: "SAP S/4HANA",
    color: "#0D47A1",
    category: "ERP",
    description: "SAP S/4HANA integration",
    get Settings() {
      return SapS4HanaSettings;
    },
  },
};

const defaultMeta: IntegrationMeta = {
  shortName: "?",
  color: "#9E9E9E",
  category: "",
  description: "",
};

export function getIntegrationMeta(integrationId: string): IntegrationMeta {
  return integrationRegistry[integrationId] ?? defaultMeta;
}
